import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Clock, Users, Filter } from 'lucide-react';
import { persistedClientRecords } from '../../services/mockData/clients';
import { persistedCaseRecords } from '../../services/mockData/cases';
import { useGobodoRecords, useRecordCount } from '../../services/queries/gobodoRecordQueries';
import {
  caseDueDate,
  caseDueSoonForMyWork,
  fetchUnlockRequestsLinkedToCase,
  isCaseOpenForMyWork,
  parseCaseDateForMyWork,
  parseUnlockRequestDueBounds,
  useCreditBlockAssignedCasesForMyWork,
} from '../../services/queries/creditBlockCaseQueries';
import { useCreditBlockClientCount, useCreditBlockClientsPaginated } from '../../services/queries/creditBlockQueries';
import { looksLikeGraphUserSfId } from '../../services/gobodo/gobodoUserSfIdLookup';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import type { ListViewRecord } from '../../types/listView';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { CheckForNewClientsButton } from './CheckForNewClientsButton';
import {
  CREDIT_BLOCK_TEAM_USERS_QUERY_KEY,
  fetchCreditBlockTeamUsers,
  getCreditBlockRoleLabel,
  hasCreditBlockTeamRole,
  isCreditBlockTeamRoleName,
} from '../../services/creditBlock/creditBlockAssigneePickerUtils';
import { getUserFullName } from '../../services/queries/userQueries';
import { usersToCreditBlockAssigneeOptions } from '../../services/gobodo/creditBlockNewCaseGobodoSearch';
import PendingArrayApprovalsPanel from './PendingArrayApprovalsPanel';
export const CREDIT_BLOCK_OPS_HOME_MODE_STORAGE_KEY = 'credit-block-ops-home-mode';

export type CreditBlockOpsHomeMode = 'processor' | 'manager';

export function readCreditBlockOpsHomeMode(userRole: string): CreditBlockOpsHomeMode {
  try {
    const v = localStorage.getItem(CREDIT_BLOCK_OPS_HOME_MODE_STORAGE_KEY);
    if (v === 'processor' || v === 'manager') return v;
  } catch {
    /* ignore */
  }
  return ['manager', 'system_admin', 'cb_supervisor'].includes(userRole) ? 'manager' : 'processor';
}

function matchesCurrentUser(assignee: string, userName: string, userId: string) {
  const a = assignee.trim();
  if (!a) return false;
  const un = userName.trim();
  const uid = userId.trim();
  if (a === uid) return true;
  if (un && a === un) return true;
  if (un && a.toLowerCase() === un.toLowerCase()) return true;
  return false;
}

/** Regal Home–style “My work” (no GlobalNavigation). */
const FILTER_SELECT = 'px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100';

// Removed local fetchCreditBlockTeamUsers — now imported from
// creditBlockAssigneePickerUtils as the canonical shared resolver. All
// three CB consumers (Team Dashboard, Unassigned Queue, Case Detail owner
// picker) share the same React Query cache via CREDIT_BLOCK_TEAM_USERS_QUERY_KEY.

/**
 * Subscribes to User→Case assignee traversal on Credit Block Overview even when "Team dashboard" is selected
 * (My Work is unmounted in that mode, but we still want `traverseObjects` for cache + switching back to My work).
 */
export const CreditBlockOverviewAssigneeTraversalPrefetch: React.FC = () => {
  const gobodo = shouldUseGobodo();
  useCreditBlockAssignedCasesForMyWork(gobodo);
  return null;
};

function caseStatus(c: ListViewRecord) {
  return String(c.status ?? (c as Record<string, unknown>).Status__c ?? '').trim();
}

function casePriority(c: ListViewRecord) {
  return String(c.priority ?? (c as Record<string, unknown>).Priority__c ?? '').trim();
}

function caseSubType(c: ListViewRecord) {
  return String(c.subType ?? (c as Record<string, unknown>).Sub_Type__c ?? '').trim();
}

function caseHeading(c: ListViewRecord) {
  const num =
    String(c.caseNumber ?? '').trim() ||
    String((c as Record<string, unknown>).numericObjectId ?? c.id ?? '').trim();
  const subj = String(c.subject ?? '').trim();
  if (num && subj) return `${num} · ${subj}`;
  return subj || num || `Case ${c.id}`;
}

/** Prefer Unlock_Request end/start dates when loaded; else Case `caseDueDate`. */
function unlockOrCaseDueLabel(
  caseRow: ListViewRecord,
  openCases: ListViewRecord[],
  unlockQueryRows: Array<ListViewRecord[] | undefined>
): string {
  const idx = openCases.findIndex((x) => String(x.id) === String(caseRow.id));
  const rows = idx >= 0 ? (unlockQueryRows[idx] ?? []) : [];
  if (rows.length === 0) return caseDueDate(caseRow) || '—';
  let best: Date | null = null;
  for (const row of rows) {
    const { end, start } = parseUnlockRequestDueBounds(row as Record<string, unknown>);
    const pick = end ?? start;
    if (pick && (!best || pick.getTime() < best.getTime())) best = pick;
  }
  if (best) return best.toISOString().slice(0, 10);
  return caseDueDate(caseRow) || '—';
}

/**
 * My Work: lists are **Cases** from User→Case assignee traversal.
 */
export const CreditBlockMyWorkOverview: React.FC<{
  /** Kept for API compatibility; My Work navigates via `onCaseSelect` (cases). */
  onTaskSelect?: (id: string) => void;
  onCaseSelect?: (caseId: string) => void;
}> = (props) => {
  const { onCaseSelect } = props;
  const navigate = useNavigate();
  const { userName, userId } = useCurrentUser();
  const shouldGobodo = shouldUseGobodo();
  const { data: assignedCasesFromApi = [], isPending: assignedCasesPending, isError: assignedCasesError } =
    useCreditBlockAssignedCasesForMyWork(shouldGobodo);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const baseCases = useMemo((): ListViewRecord[] => {
    if (shouldGobodo) {
      if (assignedCasesPending) return [];
      return assignedCasesFromApi;
    }
    return persistedCaseRecords
      .get()
      .filter((c) => matchesCurrentUser(String(c.owner ?? '').trim(), userName, userId));
  }, [shouldGobodo, assignedCasesPending, assignedCasesFromApi, userName, userId]);

  const filteredCases = useMemo(() => {
    return baseCases.filter((c) => {
      const st = caseStatus(c);
      const pr = casePriority(c);
      if (statusFilter !== 'all' && st !== statusFilter) return false;
      if (priorityFilter !== 'all' && pr !== priorityFilter) return false;
      return true;
    });
  }, [baseCases, statusFilter, priorityFilter]);

  const assigneeWorkStillLoading = shouldGobodo && assignedCasesPending && !assignedCasesError;

  const openCases = useMemo(() => filteredCases.filter((c) => isCaseOpenForMyWork(c)), [filteredCases]);

  const CASE_UNLOCK_STALE_MS = 180_000;
  const unlockQueries = useQueries({
    queries: openCases.map((c) => {
      const id = String(c.id ?? '').trim();
      return {
        queryKey: ['credit-block', 'case-unlock-requests', id] as const,
        queryFn: () => fetchUnlockRequestsLinkedToCase(id),
        enabled: shouldGobodo && Boolean(id),
        staleTime: CASE_UNLOCK_STALE_MS,
      };
    }),
  });

  const dueSoonCases = useMemo(() => {
    return openCases.filter((c, i) => {
      if (!shouldGobodo) {
        return caseDueSoonForMyWork([], c, 3);
      }
      const q = unlockQueries[i];
      const rows = (q?.data ?? []) as ListViewRecord[];
      const settled = q?.isSuccess === true || q?.isError === true;
      if (!settled) {
        return caseDueSoonForMyWork([], c, 3);
      }
      return caseDueSoonForMyWork(rows, c, 3);
    });
  }, [shouldGobodo, openCases, unlockQueries]);

  const unlockRowsByOpenCaseIndex = useMemo(
    () => unlockQueries.map((q) => q.data as ListViewRecord[] | undefined),
    [unlockQueries]
  );

  const completedCases = useMemo(
    () =>
      filteredCases.filter((c) => {
        const s = caseStatus(c);
        return s === 'Closed' || s === 'Resolved';
      }),
    [filteredCases]
  );

  const highPriorityOpenCases = useMemo(
    () =>
      openCases.filter((c) => {
        const p = casePriority(c);
        return p === 'High' || p === 'Critical';
      }),
    [openCases]
  );

  const recentActivityCases = useMemo(() => {
    return [...filteredCases]
      .sort((a, b) => {
        const tb = parseCaseDateForMyWork(a.lastModifiedDate)?.getTime() ?? 0;
        const ta = parseCaseDateForMyWork(b.lastModifiedDate)?.getTime() ?? 0;
        return ta - tb;
      })
      .slice(0, 8);
  }, [filteredCases]);

  const goToCase = (id: string) =>
    onCaseSelect ? onCaseSelect(id) : navigate(`/cases/${id}`);

  const priorityBadgeClass = (p: string) =>
    p === 'High' || p === 'Critical'
      ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
      : p === 'Medium'
        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
        : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';

  const recentStatusDot = (st: string) => {
    const s = st.toLowerCase();
    if (s === 'closed' || s === 'resolved') return 'bg-green-500';
    if (s === 'in progress') return 'bg-blue-500';
    if (s === 'cancelled') return 'bg-red-500';
    return 'bg-yellow-500';
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Work</h2>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500 shrink-0" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={FILTER_SELECT}>
            <option value="all">All statuses</option>
            <option value="New">New</option>
            <option value="Assigned">Assigned</option>
            <option value="In Progress">In Progress</option>
            <option value="Escalated">Escalated</option>
            <option value="On Hold">On Hold</option>
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className={FILTER_SELECT}>
            <option value="all">All priorities</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Open Cases', value: assigneeWorkStillLoading ? '—' : String(openCases.length), color: 'border-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/20' },
          { label: 'Due Soon', value: assigneeWorkStillLoading ? '—' : String(dueSoonCases.length), color: 'border-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/20' },
          { label: 'Completed', value: assigneeWorkStillLoading ? '—' : String(completedCases.length), color: 'border-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/20' },
          { label: 'High Priority', value: assigneeWorkStillLoading ? '—' : String(highPriorityOpenCases.length), color: 'border-red-500', bg: 'bg-red-50 dark:bg-red-950/20' },
        ].map((card) => (
          <div key={card.label} className={`${card.bg} border-l-4 ${card.color} rounded-lg px-4 py-4`}>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{card.label}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{card.value}</p>
          </div>
        ))}
      </div>

      {/* All Cases — unified list */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            My Cases
            {!assigneeWorkStillLoading && <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">({filteredCases.length})</span>}
          </h3>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {filteredCases.slice(0, 15).map((c) => {
            const st = caseStatus(c);
            const pr = casePriority(c);
            const due = caseDueDate(c);
            const isDueSoon = dueSoonCases.some((d) => d.id === c.id);
            const lm = c.lastModifiedDate
              ? parseCaseDateForMyWork(c.lastModifiedDate)?.toLocaleDateString() ?? ''
              : '';
            return (
              <button
                key={String(c.id)}
                type="button"
                onClick={() => goToCase(String(c.id))}
                className="w-full flex items-center gap-4 px-6 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer text-left"
              >
                <div className={`flex-shrink-0 w-2 h-2 rounded-full ${recentStatusDot(st)}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{caseHeading(c)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {caseSubType(c) || String(c.type ?? '').trim() || 'Case'}
                    {c.accountName ? ` · ${String(c.accountName)}` : ''}
                    {lm ? ` · Updated ${lm}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isDueSoon && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                      Due {due || 'soon'}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${priorityBadgeClass(pr)}`}>
                    {pr || '—'}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 w-20 text-right">
                    {st || '—'}
                  </span>
                </div>
              </button>
            );
          })}
          {!assigneeWorkStillLoading && filteredCases.length === 0 && !assignedCasesError && (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">No cases assigned to you</p>
          )}
          {assignedCasesError && (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">Unable to load your cases.</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Check your connection or try refreshing the page.</p>
            </div>
          )}
          {assigneeWorkStillLoading && (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">Loading…</p>
          )}
        </div>
      </div>
    </div>
  );
};

function clientStatus(c: Record<string, unknown>): 'active' | 'suspended' | 'cancelled' | 'inactive' | 'unknown' {
  const data = (c.data && typeof c.data === 'object') ? (c.data as Record<string, unknown>) : {};
  const raw = String(
    c.subscriptionStatus ??
      c.SubscriptionStatus__c ??
      data.subscriptionStatus ??
      data.SubscriptionStatus__c ??
      c.status ??
      c.Status__c ??
      data.status ??
      data.Status__c ??
      c.Stage__c ??
      data.Stage__c ??
      ''
  )
    .trim()
    .toLowerCase();
  if (!raw) return 'unknown';
  if (raw.includes('active') || raw.includes('current client')) return 'active';
  if (raw.includes('suspended') || raw.includes('on hold') || raw.includes('nsf')) return 'suspended';
  if (raw.includes('cancel')) return 'cancelled';
  if (raw.includes('inactive')) return 'inactive';
  return 'unknown';
}

function pickFirstText(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
}

function looksLikeRecordId(value: string): boolean {
  const text = String(value ?? '').trim();
  if (!text) return false;
  if (/^\d+$/.test(text)) return true;
  if (/^[A-Za-z0-9]{15,18}$/.test(text) && /^0[0-9A-Za-z]{2}/.test(text)) return true;
  return text.length > 20 && /^[A-Za-z0-9_-]+$/.test(text);
}

function looksLikeQueueOrGroupId(value: string): boolean {
  const text = String(value ?? '').trim();
  if (!text) return false;
  return /^00G[a-z0-9]{12,15}$/i.test(text);
}

/** Regal /dashboard–style team view (no GlobalNavigation). */
export const CreditBlockTeamOverview: React.FC<{ onTaskSelect?: (id: string) => void; onCaseSelect?: (caseId: string) => void }> = ({ onTaskSelect, onCaseSelect }) => {
  const navigate = useNavigate();
  const shouldGobodo = shouldUseGobodo();
  const { data: gobodoCases } = useGobodoRecords('Case__c');
  const { data: gobodoClients } = useGobodoRecords('Client__c');
  const { data: caseCount } = useRecordCount('Case__c');
  const { data: clientCount } = useRecordCount('Client__c');
  const { data: creditBlockClientCount } = useCreditBlockClientCount(shouldGobodo);
  const { data: activeClientsPage } = useCreditBlockClientsPaginated(1, 1, 'active', shouldGobodo);
  const { data: creditBlockTeamUsers = [] } = useQuery({
    queryKey: CREDIT_BLOCK_TEAM_USERS_QUERY_KEY,
    queryFn: fetchCreditBlockTeamUsers,
    enabled: shouldGobodo,
    staleTime: 5 * 60_000,
  });
  const [teamStatusFilter, setTeamStatusFilter] = useState('all');
  const [teamPriorityFilter, setTeamPriorityFilter] = useState('all');
  const [teamAssigneeFilter, setTeamAssigneeFilter] = useState('all');

  // Dashboard is driven entirely by Cases (not Tasks)
  const allCases = (shouldGobodo && gobodoCases ? gobodoCases : []) as Record<string, unknown>[];
  const cbCases = useMemo(() => allCases.filter((c) => {
    const cType = String(c.type ?? c.Type__c ?? '').trim();
    return !cType || cType === 'Credit Block' || cType === 'Reblock' || cType === 'Onboarding';
  }), [allCases]);
  const dashboardClients = (shouldGobodo && gobodoClients ? gobodoClients : []) as Record<string, unknown>[];

  const cStatus = (c: Record<string, unknown>) => String(c.status ?? c.Status__c ?? '');
  const cPriority = (c: Record<string, unknown>) => String(c.priority ?? c.Priority__c ?? '');

  // Extract raw owner IDs from cases
  const getAssigneeRaw = (c: Record<string, unknown>) =>
    String(c.owner ?? c.Owner__c ?? c.OwnerName__c ?? c.OwnerId ?? c.OwnerId__c ?? '').trim();

  const getExplicitAssigneeName = (c: Record<string, unknown>) =>
    pickFirstText(
      c.owner_name__stg3_c,
      c.owner_name,
      c.AccountOwner__c,
      c.OwnerName__c,
      c.OwnerName,
      c.ownerName
    );

  // Collect unique SF user IDs that need name resolution
  const sfUserIdsToResolve = useMemo(() => {
    const ids: string[] = [];
    const seen = new Set<string>();
    for (const c of cbCases) {
      const explicitName = getExplicitAssigneeName(c);
      if (explicitName && !looksLikeRecordId(explicitName)) continue;
      const raw = getAssigneeRaw(c);
      if (raw && !seen.has(raw) && looksLikeGraphUserSfId(raw) && !looksLikeQueueOrGroupId(raw)) {
        ids.push(raw);
        seen.add(raw);
      }
    }
    return ids;
  }, [cbCases]);

  // Batch resolve SF user IDs to display names
  const { data: userNameMap } = useQuery({
    queryKey: ['team-dashboard-user-names', sfUserIdsToResolve.join(',')],
    queryFn: async () => {
      if (sfUserIdsToResolve.length === 0) return new Map<string, string>();
      const { resolveGraphUserNamesBySfIds } = await import('../../services/gobodo/gobodoUserSfIdLookup');
      return resolveGraphUserNamesBySfIds(sfUserIdsToResolve);
    },
    enabled: sfUserIdsToResolve.length > 0,
    staleTime: 5 * 60_000,
  });
  const userNameLookup = useMemo(() => {
    if (userNameMap instanceof Map) return userNameMap as Map<string, string>;
    if (userNameMap && typeof userNameMap === 'object') {
      return new Map(
        Object.entries(userNameMap as Record<string, unknown>).map(([k, v]) => [k, String(v ?? '')])
      );
    }
    return new Map<string, string>();
  }, [userNameMap]);

  // Get assignee display name (resolved or raw)
  const getAssignee = (t: Record<string, unknown>) => {
    const explicitName = getExplicitAssigneeName(t);
    if (explicitName && !looksLikeRecordId(explicitName)) return explicitName;
    const raw = getAssigneeRaw(t);
    return userNameLookup.get(raw) || (raw && !looksLikeRecordId(raw) ? raw : looksLikeQueueOrGroupId(raw) ? 'Queue' : raw);
  };

  const dashboardCases = useMemo(() => cbCases.filter((c) => {
    const st = String(c.status ?? c.Status__c ?? '');
    const pr = String(c.priority ?? c.Priority__c ?? '');
    const assignee = getAssignee(c);
    if (teamStatusFilter !== 'all' && st !== teamStatusFilter) return false;
    if (teamPriorityFilter !== 'all' && pr !== teamPriorityFilter) return false;
    if (teamAssigneeFilter !== 'all' && assignee !== teamAssigneeFilter) return false;
    return true;
  }), [cbCases, teamStatusFilter, teamPriorityFilter, teamAssigneeFilter, userNameLookup]);

  const totalCasesCount = (typeof caseCount === 'number' ? caseCount : null) ?? cbCases.length;
  const totalClients = (typeof clientCount === 'number' ? clientCount : null) ?? dashboardClients.length;
  const closedCases = dashboardCases.filter((c) => cStatus(c) === 'Closed' || cStatus(c) === 'Resolved').length;
  const highPriorityCases = dashboardCases.filter((c) => {
    const p = cPriority(c);
    return (p === 'High' || p === 'Critical') && cStatus(c) !== 'Closed';
  }).length;
  const openCases = dashboardCases.filter((c) => {
    const st = cStatus(c).toLowerCase();
    return st && st !== 'closed' && st !== 'resolved' && st !== 'cancelled';
  }).length;
  const activeClients = shouldGobodo
    ? (() => {
        const activeCount = activeClientsPage?.totalCount ?? 0;
        if (activeCount > 0) return activeCount;
        return creditBlockClientCount ?? 0;
      })()
    : dashboardClients.filter((c) => clientStatus(c) === 'active').length;
  const teamStats = {
    totalCases: totalCasesCount,
    closedCases,
    completionRate: totalCasesCount > 0 ? Math.round((closedCases / totalCasesCount) * 100) : 0,
    highPriorityCases,
    openCases,
    activeClients,
    totalClients,
  };

  const teamMembers = useMemo(() => {
    const byName = new Map<string, string>();
    for (const user of creditBlockTeamUsers) {
      const name = getUserFullName(user).trim() || user.email || user.username;
      if (!name) continue;
      const roles = (user.roleNames ?? []).filter(isCreditBlockTeamRoleName);
      byName.set(name, roles.join(', ') || 'Credit Block role assignment');
    }
    return Array.from(byName, ([name, roleLabel]) => ({ name, roleLabel }));
  }, [creditBlockTeamUsers]);
  const teamUserNames = useMemo(() => teamMembers.map((member) => member.name), [teamMembers]);
  const caseOwnerNames = useMemo(
    () => Array.from(new Set(cbCases.map(getAssignee).filter(Boolean))),
    [cbCases, userNameLookup]
  );
  const performanceNames = teamUserNames.length > 0 ? teamUserNames : caseOwnerNames;
  const uniqueAssignees = new Set(performanceNames);
  const teamPerformance = performanceNames.map((name) => {
    const cases = dashboardCases.filter((c) => getAssignee(c) === name);
    const closed = cases.filter((c) => cStatus(c) === 'Closed' || cStatus(c) === 'Resolved').length;
    const total = cases.length;
    return {
      name,
      completed: closed,
      total,
      completionRate: total > 0 ? Math.round((closed / total) * 100) : 0,
      source: teamUserNames.includes(name) ? 'CB role member' : 'Case owner fallback',
      sourceDetail: teamMembers.find((member) => member.name === name)?.roleLabel ?? 'Case owner fallback',
      highPriority: cases.filter((c) => {
        const p = cPriority(c);
        return (p === 'High' || p === 'Critical') && cStatus(c) !== 'Closed';
      }).length,
    };
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Team Dashboard</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CheckForNewClientsButton />
          <Filter className="h-4 w-4 text-gray-500 shrink-0" />
          <select value={teamStatusFilter} onChange={(e) => setTeamStatusFilter(e.target.value)} className={FILTER_SELECT}>
            <option value="all">All statuses</option>
            <option value="New">New</option>
            <option value="In Progress">In Progress</option>
            <option value="Resolved">Resolved</option>
            <option value="Closed">Closed</option>
          </select>
          <select value={teamPriorityFilter} onChange={(e) => setTeamPriorityFilter(e.target.value)} className={FILTER_SELECT}>
            <option value="all">All priorities</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
          <select value={teamAssigneeFilter} onChange={(e) => setTeamAssigneeFilter(e.target.value)} className={FILTER_SELECT}>
            <option value="all">All assignees</option>
            {Array.from(uniqueAssignees).sort().map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Completion Rate', value: `${teamStats.completionRate}%`, sub: `${teamStats.closedCases} of ${teamStats.totalCases} cases`, color: 'border-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/20' },
          { label: 'Active Clients', value: String(teamStats.activeClients), sub: `of ${teamStats.totalClients} total`, color: 'border-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/20' },
          { label: 'High Priority', value: String(teamStats.highPriorityCases), sub: 'urgent cases', color: 'border-red-500', bg: 'bg-red-50 dark:bg-red-950/20' },
          { label: 'Open Cases', value: String(teamStats.openCases), sub: 'in progress', color: 'border-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/20' },
        ].map((card) => (
          <div key={card.label} className={`${card.bg} border-l-4 ${card.color} rounded-lg px-4 py-4`}>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{card.label}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{card.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{card.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Team Performance</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                CB role members: {teamUserNames.length} · Case owners: {caseOwnerNames.length}
              </span>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {teamPerformance.map((member) => (
                <div key={member.name} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {member.name}
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          member.source === 'CB role member'
                            ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200'
                            : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200'
                        }`}>
                          {member.source === 'CB role member' ? member.sourceDetail : member.source}
                        </span>
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{member.completionRate}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${member.completionRate}%` }} />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>
                        {member.completed}/{member.total} completed
                      </span>
                      {member.highPriority > 0 && (
                        <span className="text-red-600 dark:text-red-400">{member.highPriority} high priority</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Priority Distribution</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {(['High', 'Medium', 'Low'] as const).map((priority) => {
                const count = dashboardCases.filter((c) => cPriority(c) === priority && cStatus(c) !== 'Closed').length;
                const total = dashboardCases.filter((c) => cStatus(c) !== 'Closed').length;
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div key={priority} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{priority} Priority</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{count} cases</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            priority === 'High' ? 'bg-red-500' : priority === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* F3: Pending Credit Pull Approvals — sits directly above Recent Cases on
          the Team Dashboard since only approvers (cb_supervisor / System
          Administrator) act on these. Renders null for non-approvers so the
          layout stays clean for processors who happen to access the Team view. */}
      <PendingArrayApprovalsPanel />

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Cases</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
              {[...dashboardCases]
                .sort((a, b) => {
                  const dateA = new Date(
                    String(a.createdDate ?? (a as Record<string, unknown>).CreatedDate__c ?? (a as Record<string, unknown>).Request_Date__c ?? '')
                  ).getTime();
                  const dateB = new Date(
                    String(b.createdDate ?? (b as Record<string, unknown>).CreatedDate__c ?? (b as Record<string, unknown>).Request_Date__c ?? '')
                  ).getTime();
                  return dateB - dateA;
                })
                .slice(0, 10)
                .map((c) => {
                  const cId = String(c.id ?? (c as Record<string, unknown>).key ?? '');
                  const cSubject = String(c.subject ?? (c as Record<string, unknown>).Subject__c ?? (c as Record<string, unknown>).label ?? '').trim();
                  const cOwner = getAssignee(c);
                  const cStatus = String(c.status ?? (c as Record<string, unknown>).Status__c ?? '').trim();
                  const cPriority = String(c.priority ?? (c as Record<string, unknown>).Priority__c ?? '').trim();
                  const cCreated = String(c.createdDate ?? (c as Record<string, unknown>).CreatedDate__c ?? (c as Record<string, unknown>).Request_Date__c ?? '').trim().slice(0, 10);
                  return (
                  <tr
                    key={cId}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    onClick={() => cId && (onCaseSelect ? onCaseSelect(cId) : navigate(`/credit-block/case/${cId}`))}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {cSubject || `Case ${cId.slice(-8)}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cOwner}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          cStatus === 'Closed' || cStatus === 'Resolved'
                            ? 'bg-green-100 text-green-800'
                            : cStatus === 'In Progress'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {cStatus || 'New'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          cPriority === 'High' || cPriority === 'Critical'
                            ? 'bg-red-100 text-red-800'
                            : cPriority === 'Medium'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {cPriority || 'Normal'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cCreated || '—'}
                    </td>
                  </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Unassigned Case Queue (Manager View)                               */
/* ------------------------------------------------------------------ */

const CASE_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';

export const CreditBlockUnassignedCaseQueue: React.FC<{
  onCaseSelect?: (caseId: string) => void;
  onAssign?: (caseId: string, assigneeName: string, assigneeUserId?: string) => void;
}> = ({ onCaseSelect, onAssign }) => {
  const navigate = useNavigate();
  const gobodo = shouldUseGobodo();
  const [assigningCaseId, setAssigningCaseId] = useState<string | null>(null);
  const [assigningCaseLabel, setAssigningCaseLabel] = useState('');
  const [assignSearchQuery, setAssignSearchQuery] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignToast, setAssignToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const assignInputRef = useRef<HTMLInputElement>(null);
  const { data: allCases = [], isPending } = useGobodoRecords(`Case${CASE_SUFFIX}`, { enabled: gobodo });
  const { data: assignTeamUsers = [], isLoading: assignTeamUsersLoading } = useQuery({
    queryKey: CREDIT_BLOCK_TEAM_USERS_QUERY_KEY,
    queryFn: fetchCreditBlockTeamUsers,
    enabled: Boolean(assigningCaseId),
    staleTime: 5 * 60_000,
  });
  const assignTeamOptions = useMemo(
    () => {
      const roleByValue = new Map(
        assignTeamUsers.map((user) => [
          String(user.graphObjectKey ?? user.id ?? '').trim(),
          getCreditBlockRoleLabel(user),
        ])
      );
      return usersToCreditBlockAssigneeOptions(assignTeamUsers).map((option) => ({
        ...option,
        value: option.value.replace(/^u:/, ''),
        roleLabel: roleByValue.get(option.value.replace(/^u:/, '')) || 'Credit Block role',
      }));
    },
    [assignTeamUsers]
  );
  const filteredAssignOptions = useMemo(() => {
    const query = assignSearchQuery.trim().toLowerCase();
    if (!query) return assignTeamOptions;
    return assignTeamOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [assignSearchQuery, assignTeamOptions]);

  const unassignedCases = useMemo(() => {
    return allCases
      .filter((c) => {
        const owner = String(c.owner ?? c.Owner__c ?? c.OwnerId ?? '').trim();
        const caseType = String(c.type ?? c.Type__c ?? '').trim();
        const status = String(c.status ?? c.Status__c ?? '').trim().toLowerCase();
        // Show Credit Block cases that are unassigned and not closed
        return (
          (!owner || owner === '—' || owner === 'undefined') &&
          status !== 'closed' &&
          (caseType === 'Credit Block' || caseType === 'Reblock' || caseType === 'Onboarding' || !caseType)
        );
      })
      .sort((a, b) => {
        const da = String(a.createdDate ?? a.CreatedDate__c ?? '');
        const db = String(b.createdDate ?? b.CreatedDate__c ?? '');
        return db.localeCompare(da);
      });
  }, [allCases]);

  const handleRowClick = (c: Record<string, unknown>) => {
    const id = String(c.id ?? c.key ?? '').trim();
    if (onCaseSelect) {
      onCaseSelect(id);
    } else if (id) {
      navigate(`/credit-block/case/${id}`);
    }
  };

  // Auto-focus the search input when modal opens
  useEffect(() => {
    if (assigningCaseId) {
      setTimeout(() => assignInputRef.current?.focus(), 50);
    }
  }, [assigningCaseId]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!assignToast) return;
    const t = setTimeout(() => setAssignToast(null), 3000);
    return () => clearTimeout(t);
  }, [assignToast]);

  const closeAssignModal = () => {
    setAssigningCaseId(null);
    setAssigningCaseLabel('');
    setAssignSearchQuery('');
  };

  const handleAssignSelect = async (caseId: string, assignee: { value: string; label: string }) => {
    if (!onAssign) return;
    setAssignSaving(true);
    try {
      await onAssign(caseId, assignee.label, assignee.value);
      setAssignToast({ message: `Case assigned to ${assignee.label}`, type: 'success' });
      closeAssignModal();
    } catch {
      setAssignToast({ message: 'Failed to assign case', type: 'error' });
    } finally {
      setAssignSaving(false);
    }
  };

  if (isPending) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Unassigned Case Queue</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">Loading cases...</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Unassigned Case Queue</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {unassignedCases.length} case{unassignedCases.length !== 1 ? 's' : ''} awaiting assignment
          </p>
        </div>
        {unassignedCases.length > 0 && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs font-semibold rounded-full">
            <AlertCircle className="h-3.5 w-3.5" />
            {unassignedCases.length}
          </span>
        )}
      </div>
      {unassignedCases.length === 0 ? (
        <div className="px-6 py-8 text-center">
          <CheckCircle className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">All cases are assigned</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Case</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sub-Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {unassignedCases.map((c) => {
                const id = String(c.id ?? c.key ?? '');
                const subj = String(c.subject ?? c.Subject__c ?? c.label ?? '').trim();
                const subType = String(c.subType ?? c.Sub_Type__c ?? '').trim();
                const priority = String(c.priority ?? c.Priority__c ?? '').trim();
                const created = String(c.createdDate ?? c.CreatedDate__c ?? '').trim().slice(0, 10);
                return (
                  <tr
                    key={id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => handleRowClick(c)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
                        {subj || `Case ${id.slice(-8)}`}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{subType || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          priority === 'High' || priority === 'Critical'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            : priority === 'Medium'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        }`}
                      >
                        {priority || 'Normal'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{created || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAssigningCaseId(id);
                          setAssigningCaseLabel(subj || `Case ${id.slice(-8)}`);
                        }}
                        className="px-3 py-1.5 text-xs font-medium border border-blue-200 dark:border-blue-700 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                      >
                        Assign
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign modal */}
      {assigningCaseId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={closeAssignModal}>
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Assign Case</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">{assigningCaseLabel}</p>
            </div>
            <div className={`px-6 py-4 ${assignSaving ? 'opacity-50 pointer-events-none' : ''}`}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Select team member</label>
              <input
                ref={assignInputRef}
                type="text"
                value={assignSearchQuery}
                onChange={(e) => setAssignSearchQuery(e.target.value)}
                placeholder="Filter by name..."
                className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={assignSaving}
              />
              <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                {assignTeamUsersLoading ? (
                  <p className="px-4 py-6 text-sm text-gray-400 text-center">Loading CB team members...</p>
                ) : filteredAssignOptions.length > 0 ? (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {filteredAssignOptions.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => handleAssignSelect(assigningCaseId, r)}
                        className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-900 dark:text-gray-100 transition-colors"
                      >
                        <span className="block">{r.label}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">{r.roleLabel}</span>
                      </button>
                    ))}
                  </div>
                ) : assignSearchQuery.trim() ? (
                  <p className="px-4 py-6 text-sm text-gray-400 text-center">No matching CB team members found</p>
                ) : (
                  <p className="px-4 py-6 text-sm text-gray-400 text-center">
                    No active CB team members found from Credit Block role assignments
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                type="button"
                onClick={closeAssignModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {assignToast && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            assignToast.type === 'success'
              ? 'bg-emerald-600 text-white'
              : 'bg-red-600 text-white'
          }`}>
            {assignToast.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            {assignToast.message}
            <button onClick={() => setAssignToast(null)} className="ml-2 hover:opacity-70">&times;</button>
          </div>
        </div>
      )}
    </div>
  );
};
